// Utility functions for reference handling in academic articles
import { faSection } from '@fortawesome/pro-regular-svg-icons'
import { setAnchorAttrs } from './tooltipUtils'
import { MARKING_EXCLUDED_ATTRIBUTE } from '#shared/utils/markings/rules'

export interface ReferenceCardMap {
  refCardMap: WeakMap<Element, HTMLElement>
  addCard: (ref: HTMLElement, card: HTMLElement) => void
  removeCard: (ref: HTMLElement) => void
  getCard: (ref: HTMLElement) => HTMLElement | undefined
}

export function createReferenceCardMap(): ReferenceCardMap {
  const refCardMap = new WeakMap<Element, HTMLElement>()

  return {
    refCardMap,
    addCard: (ref: HTMLElement, card: HTMLElement) => {
      refCardMap.set(ref, card)
    },
    removeCard: (ref: HTMLElement) => {
      const existing = refCardMap.get(ref)
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing)
        refCardMap.delete(ref)
      }
    },
    getCard: (ref: HTMLElement) => {
      return refCardMap.get(ref)
    },
  }
}

export function createReferenceCard(ref: HTMLElement, details: HTMLElement): HTMLElement {
  const card = document.createElement('span')
  card.className = 'reference-card block my-3 rounded-md bg-blue-50 reading-dark:bg-carbon-50 text-neutral-black font-body text-sm leading-6 p-s'
  // Injected into the article body, so markings must treat it as chrome.
  card.setAttribute(MARKING_EXCLUDED_ATTRIBUTE, '')

  // Header with title and close button
  const header = document.createElement('div')
  header.className = 'reference-card-header flex items-center justify-between mb-2xs'

  const titleEl = document.createElement('span')
  titleEl.className = 'reference-card-title text-caption leading-4.5 font-bold'
  titleEl.textContent = 'Ref'

  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.setAttribute('aria-label', 'Close reference')
  closeBtn.className = 'reference-card-close text-secondary reading-dark:text-carbon-500 reading-dark:hover:text-neutral-white pb-[1px] text-body font-regular border border-black hover:border-0 rounded-lg aspect-square w-8 hover:bg-surface-secondary reading-dark:hover:bg-surface-hover active:bg-surface-invert active:text-neutral-white'
  closeBtn.textContent = '×'

  header.appendChild(titleEl)
  header.appendChild(closeBtn)

  // Body with details
  const body = document.createElement('div')
  body.className = 'reference-card-body text-small'
  body.innerHTML = details.innerHTML

  card.appendChild(header)
  card.appendChild(body)

  // Ensure links inside the card also open in new tabs
  setAnchorAttrs(card)

  return card
}

export function isReferenceElement(element: Element): boolean {
  return element.classList.contains('reference') && element.tagName === 'SPAN'
}

export function isReferenceButton(element: Element): boolean {
  return element.classList.contains('reference') && element.tagName === 'BUTTON'
}

function createFaSectionSvg(isReferenced: boolean = false): SVGElement {
  const svgNS = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(svgNS, 'svg')
  const width = faSection.icon[0]
  const height = faSection.icon[1]
  const svgPathData = faSection.icon[4] as string | string[]
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
  svg.setAttribute('width', '1em')
  svg.setAttribute('height', '1.1em')
  svg.setAttribute('fill', 'currentColor')
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('focusable', 'false')
  svg.setAttribute('data-fa-section-icon', 'true')

  // Add base classes for border and padding
  let classes = 'border border-blurple-500 rounded p-[1px] transition-colors duration-200'

  // Add background color based on reference status
  if (isReferenced) {
    classes += ' bg-blurple-500 text-white group-hover:bg-surface-secondary group-hover:text-blurple-500'
  }
  else {
    classes += ' bg-transparent group-hover:bg-surface-secondary'
  }

  svg.setAttribute('class', classes)
  ;(svg.style as CSSStyleDeclaration).verticalAlign = '-0.125em'
  ;(svg.style as CSSStyleDeclaration).marginLeft = '0.25em'
  if (Array.isArray(svgPathData)) {
    svgPathData.forEach((d) => {
      const path = document.createElementNS(svgNS, 'path')
      path.setAttribute('d', d)
      svg.appendChild(path)
    })
  }
  else {
    const path = document.createElementNS(svgNS, 'path')
    path.setAttribute('d', svgPathData)
    svg.appendChild(path)
  }
  return svg
}

/**
 * Appends a faSection icon INSIDE each button.reference as the last child.
 * Safe to call multiple times – will not duplicate icons.
 */
export function injectSectionIconAfterReferenceButtons(root: Element, referencedNodeIds: string[]): void {
  const buttons = root.querySelectorAll('button.reference')
  buttons.forEach((btn) => {
    const el = btn as HTMLElement
    const existing = el.querySelector('[data-fa-section-icon="true"]')

    // Remove existing icon to ensure we always have the correct styling
    if (existing) {
      existing.remove()
    }

    // Add group class to button for group hover functionality
    if (!el.classList.contains('group')) {
      el.classList.add('group')
    }

    const nodeId = el.getAttribute('data-id')
    const isReferenced = nodeId ? referencedNodeIds.includes(nodeId) : false
    const svg = createFaSectionSvg(isReferenced)
    el.appendChild(svg)
  })
}
